import { combineReducers } from '@reduxjs/toolkit'
import { sessionSlice } from './session'
import storage from './storage'
import {
  billingPaymentsSlice,
  productsSlice,
  paymentsSlice,
  trialEligibilitySlice,
} from './billing'
import { twofaSlice } from './twofa'
import { defaultRuleSlice } from './defaultRule'
import { errorsSlice } from './errors'
import { servicesSlice } from './services'
import { filtersSlice } from './filters'
import { customRulesSlice, groupsSlice, ruleTraySlice } from './customRules'
import { proxyTraySlice } from 'store/proxyTray'
import { profileSlice } from 'store/multiprofile/profiles'
import { deviceSlice } from 'store/multiprofile/devices'
import { notificationsSlice } from 'store/notifications'
import { accessSlice } from 'store/access'
import { tutorialSlice } from 'store/tutorial/tutorial'
import { freeDNSSlice } from 'store/freeDNS'
import { persistReducer } from 'redux-persist'
import { analyticsSlice } from './analytics'
import { featuredProfilesSlice } from 'store/multiprofile/featuredProfiles'
import { migrateSessionsState } from './migrations'
import { trackingSlice } from 'store/tracking'
import { provisionSlice } from 'store/provisioning'
import { activityLogSlice } from 'store/activityLog'
import { dashboardSlice } from 'store/dashboard/dashboard'
import { baseApi } from 'store/api'
import { organizationSlice } from 'store/organization/organization'
import { adminLogSlice } from './adminLog'
import { barrySlice } from 'store/barry/barry'
import {
  persistDataMigrationTransform,
  persistOrgTransform,
  persistQueryFiltersTransform,
} from 'store/transforms'
import { domainTestSlice } from 'store/domainTest'

const persistTutorialConfig = {
  key: 'tutorial',
  storage,
  migrate: state => {
    return migrateSessionsState(state, 'persist:sessions')
  },
}

const persistNotificationsConfig = {
  key: 'notifications',
  storage,
  whitelist: ['savedNotificationKeysByUserPk'],
}

const persistSessionConfig = {
  key: 'session',
  storage,
  transforms: [persistOrgTransform, persistDataMigrationTransform],
  whitelist: [
    'userPk',
    'isOrganization',
    'region',
    'email',
    'hasClosedChecklist',
    'sessionToken',
    'sessionTtl',
    'wasRememberMeSelected',
  ],
}

const persistPaymentsConfig = {
  key: 'payments',
  storage,
  whitelist: ['planStepsCount', 'lastPaymentCurrencyCode'],
}

const persistProductsConfig = {
  key: 'products',
  storage,
  whitelist: ['promoMode', 'promoCode', 'selectedProductPk', 'priceInterval', 'selectedCurrency'],
}
const persistServicesConfig = {
  key: 'services',
  storage,
  whitelist: ['showActiveOnly', 'showCardView', 'numberOfServices', 'hasShownServicesWarning'],
}

export const persistDefaultRuleConfig = {
  key: 'defaultRule',
  storage,
  whitelist: ['redirectMode', 'lastUsedRedirectLocationPK'],
}

const persistProfilesConfig = {
  key: 'profiles',
  storage,
  whitelist: ['shouldSortProfilesAlphabetically', 'editingProfileSection', 'currentProfileId'],
}

export const persistAnalyticsConfig = {
  key: 'analytics',
  storage,
  whitelist: [
    'verdictTypeFilter',
    'filterParameter',
    'filterType',
    'selectedPresetTimeRange',
    'timeRange',
    'dropdownsData',
    'filterParameters',
    'queryLogData',
    'isDataAvailabilityBannerVisibleByUserPk',
  ],
}
export const persistCustomRulesConfig = {
  key: 'customRules',
  storage,
  whitelist: ['shouldShowLargeView', 'groupUserIsIn'],
}

export const persistTrackingConfig = {
  key: 'experience',
  storage,
}

const persistBarryConfig = {
  key: 'barry',
  storage,
  whitelist: ['message', 'offence', 'barryPage', 'userExperience', 'shouldShowBarry'],
}

const persistAccessConfig = {
  key: 'access',
  storage,
  whitelist: ['selectedDeviceId'],
}

const persistActivityLogConfig = {
  key: 'activityLog',
  storage,
  transforms: [persistQueryFiltersTransform],
  whitelist: ['selectedPresetTimeRange', 'queryFilters'],
}

const persistDashboardConfig = {
  key: 'dashboard',
  storage,
  whitelist: ['breakpointIndex', 'theme'],
}

const persistDevicesConfig = {
  key: 'devices',
  storage,
  whitelist: [
    'sortDevicesBy',
    'groupClientsBy',
    'shouldSortClientsAlphabetically',
    'latestCtrldVersion',
  ],
}

const persistOrganizationsConfig = {
  key: 'organizations',
  storage,
  whitelist: ['selectedSubOrgPk'],
}

const persistProvisioningConfig = {
  key: 'provisioning',
  storage,
  whitelist: ['consumption'],
}

const persistAdminLogConfig = {
  key: 'adminLog',
  storage,
  whitelist: ['logFilters', 'selectedPresetTimeRange', 'initialExternalRegionsParam'],
}

const appReducer = combineReducers({
  twofa: twofaSlice.reducer,
  errors: errorsSlice.reducer,
  groups: groupsSlice.reducer,
  access: persistReducer(persistAccessConfig, accessSlice.reducer),
  devices: persistReducer(persistDevicesConfig, deviceSlice.reducer),
  filters: filtersSlice.reducer,
  freeDNS: freeDNSSlice.reducer,
  profiles: persistReducer(persistProfilesConfig, profileSlice.reducer),
  ruleTray: ruleTraySlice.reducer,
  proxyTray: proxyTraySlice.reducer,
  billingPayments: billingPaymentsSlice.reducer,
  featuredProfiles: featuredProfilesSlice.reducer,
  trialEligibility: trialEligibilitySlice.reducer,
  domainTest: domainTestSlice.reducer,
  barry: persistReducer(persistBarryConfig, barrySlice.reducer),
  session: persistReducer(persistSessionConfig, sessionSlice.reducer),
  services: persistReducer(persistServicesConfig, servicesSlice.reducer),
  tutorial: persistReducer(persistTutorialConfig, tutorialSlice.reducer),
  products: persistReducer(persistProductsConfig, productsSlice.reducer),
  payments: persistReducer(persistPaymentsConfig, paymentsSlice.reducer),
  tracking: persistReducer(persistTrackingConfig, trackingSlice.reducer),
  analytics: persistReducer(persistAnalyticsConfig, analyticsSlice.reducer),
  defaultRule: persistReducer(persistDefaultRuleConfig, defaultRuleSlice.reducer),
  customRules: persistReducer(persistCustomRulesConfig, customRulesSlice.reducer),
  notifications: persistReducer(persistNotificationsConfig, notificationsSlice.reducer),
  provisioning: persistReducer(persistProvisioningConfig, provisionSlice.reducer),
  activityLog: persistReducer(persistActivityLogConfig, activityLogSlice.reducer),
  dashboard: persistReducer(persistDashboardConfig, dashboardSlice.reducer),
  organization: persistReducer(persistOrganizationsConfig, organizationSlice.reducer),
  adminLog: persistReducer(persistAdminLogConfig, adminLogSlice.reducer),
  [baseApi.reducerPath]: baseApi.reducer,
})

export type RootState = ReturnType<typeof appReducer>

export default appReducer
